import { ShellHeader } from '@/app/shell-header';
import { SearchField } from '@/components/search-field';
import { resourceLabel } from '@/data/groups';
import {
  RECORD_STATUSES,
  RECORDS,
  STATUS_TONE,
  type DataRecord,
  type RecordStatus,
} from '@/data/records';
import { TODAY } from '@/data/seed';
import { formatDate, formatWon } from '@/lib/format';
import { josa } from '@/lib/josa';
import { useUrlState } from '@/lib/url-state';
import {
  filterRecords,
  filtersFromQuery,
  isQueryApplied,
  parseListQuery,
  patchFromFilters,
  sortRecords,
} from '@/pages/list/list-query';
import {
  ActionMenu,
  Badge,
  BulkActionBar,
  Button,
  ConfirmDialog,
  Counter,
  DataTable,
  FilterRow,
  ListPage,
  PageHeader,
  PaginationCompact,
  type DataTableColumn,
  type FilterDef,
} from '@mgrvtechgroup/admin-ds';
import { addDays } from 'date-fns';
import { ListIcon, SearchXIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  applyStatus,
  createHistoryEntry,
  deleteRows,
  describeTargets,
  isAllSelected,
  pruneSelection,
  toNewRecords,
  type UploadHistoryEntry,
} from './bulk-model';
import { DownloadDialog } from './download-dialog';
import { UploadDialog } from './upload-dialog';

const DEFAULT_PAGE_SIZE = 25;

const FILTER_DEFS: FilterDef[] = [
  {
    key: 'status',
    label: '상태',
    pinned: true,
    options: RECORD_STATUSES.map((value) => ({ value, label: value })),
  },
];

const COLUMNS: DataTableColumn<DataRecord>[] = [
  { key: 'title', header: '이름', cell: (row) => row.title },
  {
    key: 'resource',
    header: '리소스',
    sortable: false,
    cell: (row) => resourceLabel(row.resourceId),
  },
  {
    key: 'status',
    header: '상태',
    cell: (row) => <Badge tone={STATUS_TONE[row.status]}>{row.status}</Badge>,
  },
  {
    key: 'period',
    header: '기간',
    sortKey: 'startDate',
    cellClassName: 'tabular-nums text-muted-foreground',
    cell: (row) => `${formatDate(row.startDate)} ~ ${formatDate(row.endDate)}`,
  },
  {
    key: 'amount',
    header: '금액',
    align: 'right',
    cellClassName: 'tabular-nums',
    cell: (row) => formatWon(row.amount),
  },
];

// 처음 열었을 때 "업로드 내역" 탭이 비어 보이지 않도록 과거 기록 두 건을 깔아 둔다.
const SEED_HISTORY: UploadHistoryEntry[] = [
  {
    id: 'U-seed-2',
    at: addDays(TODAY, -2).toISOString(),
    fileName: 'items-batch-02.csv',
    total: 40,
    success: 38,
    failure: 2,
  },
  {
    id: 'U-seed-1',
    at: addDays(TODAY, -9).toISOString(),
    fileName: 'items-batch-01.xlsx',
    total: 25,
    success: 25,
    failure: 0,
  },
];

type PendingAction = { kind: 'status'; status: RecordStatus } | { kind: 'delete' };

/**
 * 일괄 처리 패턴 — 표에서 여러 행을 골라 한 번에 바꾸거나 지우고, 엑셀(CSV)로 올리고 내려받는다.
 * - 선택은 페이지를 넘어도 유지된다. 헤더 체크박스는 현재 페이지만 다루고, 조건에 맞는 전체는 "N건 모두 선택"으로 고른다.
 *   다른 페이지에서 고른 행은 "선택 항목만 보기"로 모아 확인한다.
 * - 검색·필터를 바꾸면 선택을 비운다. 화면에서 사라진 행이 선택에 남아 있으면 보이지 않는 행을 지우게 되기 때문이다.
 * - 일괄 변경·삭제는 확인창(대상 건수·이름 일부·결과)을 거친 뒤 실행하고, 결과는 토스트로 알린다.
 * 데이터는 이 페이지 안에서만 바뀌는 사본이라 새로고침하면 초기화된다.
 */
export function BulkPage() {
  const { params, get, set } = useUrlState();
  const query = parseListQuery(params);

  const [rows, setRows] = useState<DataRecord[]>(() => RECORDS.map((record) => ({ ...record })));
  const [selected, setSelected] = useState<string[]>([]);
  const [pending, setPending] = useState<PendingAction | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [history, setHistory] = useState<UploadHistoryEntry[]>(SEED_HISTORY);
  const [onlySelected, setOnlySelected] = useState(false);

  const filtered = useMemo(
    () => sortRecords(filterRecords(rows, query), query.sort),
    // query는 params에서 매번 새로 만들어진다 — params로 의존성을 대신한다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, params],
  );

  const selectedSet = useMemo(() => new Set(selected), [selected]);
  const selectedRows = useMemo(
    () => rows.filter((row) => selectedSet.has(row.id)),
    [rows, selectedSet],
  );
  // 선택이 비면 "선택 항목만 보기"도 저절로 풀린다.
  const showingSelected = onlySelected && selected.length > 0;
  const visibleRows = useMemo(
    () => (showingSelected ? sortRecords(selectedRows, query.sort) : filtered),
    // query.sort는 params에서 파생 — params로 의존성을 대신한다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [showingSelected, selectedRows, filtered, params],
  );

  const pageSize = Number(get('size')) || DEFAULT_PAGE_SIZE;
  const pageCount = Math.max(1, Math.ceil(visibleRows.length / pageSize));
  const page = Math.min(Math.max(1, Number(get('page')) || 1), pageCount);
  const pageRows = visibleRows.slice((page - 1) * pageSize, page * pageSize);
  const pageAllSelected = pageRows.length > 0 && pageRows.every((row) => selectedSet.has(row.id));
  const showSelectAll = !showingSelected && pageAllSelected && !isAllSelected(selected, filtered);

  /** 조건(검색·필터)이 바뀌는 변경은 선택도 함께 비운다. */
  const changeCondition = (patch: Parameters<typeof set>[0]) => {
    set(patch);
    setSelected([]);
  };

  const runPending = () => {
    if (!pending) {
      return;
    }
    if (pending.kind === 'status') {
      const result = applyStatus(rows, selectedSet, pending.status);
      setRows(result.rows);
      toast.success(
        result.skipped > 0
          ? `${String(result.changed)}건의 상태를 ${josa(`"${pending.status}"`, '으로/로')} 변경했습니다. ${String(result.skipped)}건은 이미 같은 상태라 건너뛰었습니다.`
          : `${String(result.changed)}건의 상태를 ${josa(`"${pending.status}"`, '으로/로')} 변경했습니다.`,
      );
    } else {
      const result = deleteRows(rows, selectedSet);
      setRows(result.rows);
      setSelected(pruneSelection(selected, result.rows));
      toast.success(`${String(result.changed)}건을 삭제했습니다.`);
    }
    setPending(null);
  };

  return (
    <ListPage>
      <ShellHeader>
        <PageHeader
          title={
            <div className="flex items-center gap-2">
              <h1 className="title-md">일괄 처리</h1>
              <Counter>{rows.length}건</Counter>
            </div>
          }
          description="여러 행을 골라 상태 변경·삭제를 한 번에 하고, 엑셀(CSV)로 올리고 내려받는 패턴을 보여 주는 페이지입니다."
        />
      </ShellHeader>

      <ListPage.Toolbar
        left={
          <div className="flex w-full flex-wrap items-center gap-2">
            <SearchField
              value={query.q}
              onSearch={(value) => {
                changeCondition({ q: value, page: null });
              }}
              placeholder="이름·ID·리소스 검색"
            />
            <FilterRow
              defs={FILTER_DEFS}
              value={filtersFromQuery(query)}
              onChange={(filters) => {
                changeCondition(patchFromFilters(filters));
              }}
            />
            {isQueryApplied(query) && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  changeCondition({ q: null, status: null, page: null });
                }}
              >
                초기화
              </Button>
            )}
          </div>
        }
        right={
          <>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setUploadOpen(true);
              }}
            >
              엑셀 업로드
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setDownloadOpen(true);
              }}
            >
              엑셀 다운로드
            </Button>
          </>
        }
      />

      {selected.length > 0 && (
        <ListPage.Banner className="border-t py-2">
          <BulkActionBar
            count={selected.length}
            onClear={() => {
              setSelected([]);
            }}
          >
            <Button
              size="sm"
              variant="ghost"
              aria-pressed={showingSelected}
              onClick={() => {
                setOnlySelected(!showingSelected);
                set({ page: null });
              }}
            >
              {showingSelected ? '전체 목록 보기' : '선택 항목만 보기'}
            </Button>
            {showSelectAll && (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setSelected(filtered.map((row) => row.id));
                }}
              >
                전체 {filtered.length}건 모두 선택
              </Button>
            )}
            <ActionMenu
              label="상태 일괄 변경"
              trigger={
                <Button size="sm" variant="outline">
                  상태 변경
                </Button>
              }
              items={RECORD_STATUSES.map((status) => ({
                label: status,
                onClick: () => {
                  setPending({ kind: 'status', status });
                },
              }))}
            />
            <Button
              size="sm"
              variant="outline"
              tone="danger"
              onClick={() => {
                setPending({ kind: 'delete' });
              }}
            >
              삭제
            </Button>
          </BulkActionBar>
        </ListPage.Banner>
      )}

      <ListPage.Body className="border-t">
        <DataTable
          columns={COLUMNS}
          data={pageRows}
          rowKey={(row) => row.id}
          selectedIds={selected}
          onSelectionChange={setSelected}
          sort={query.sort}
          onSortChange={(sort) => {
            set({ sort, page: null });
          }}
          empty={
            rows.length > 0
              ? {
                  title: '조건에 맞는 항목이 없습니다',
                  description: '검색어나 필터를 바꾸거나 초기화를 눌러 보세요.',
                  icon: SearchXIcon,
                }
              : {
                  title: '항목이 없습니다',
                  description: '엑셀 업로드로 항목을 등록해 보세요.',
                  icon: ListIcon,
                }
          }
        />
      </ListPage.Body>

      {visibleRows.length > 0 && (
        <ListPage.Pagination>
          <PaginationCompact
            totalCount={visibleRows.length}
            page={page}
            pageSize={pageSize}
            onPageChange={(next) => {
              set({ page: next === 1 ? null : String(next) });
            }}
            onPageSizeChange={(size) => {
              set({ size: size === DEFAULT_PAGE_SIZE ? null : String(size), page: null });
            }}
          />
        </ListPage.Pagination>
      )}

      <ConfirmDialog
        open={pending !== null}
        onOpenChange={(open) => {
          if (!open) {
            setPending(null);
          }
        }}
        variant={pending?.kind === 'delete' ? 'destructive' : 'default'}
        target={
          pending?.kind === 'status'
            ? `선택한 ${String(selected.length)}건의 상태를 ${josa(`"${pending.status}"`, '으로/로')}`
            : `선택한 ${String(selected.length)}건을`
        }
        action={pending?.kind === 'delete' ? '삭제' : '변경'}
        consequence={
          pending?.kind === 'delete'
            ? `대상: ${describeTargets(selectedRows)}. 삭제한 항목은 되돌릴 수 없습니다.`
            : `대상: ${describeTargets(selectedRows)}. 이미 같은 상태인 항목은 건너뜁니다.`
        }
        onConfirm={runPending}
      />

      <UploadDialog
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        history={history}
        onApply={(fileName, parsed) => {
          setRows((prev) => [...toNewRecords(prev, parsed.successes), ...prev]);
          setHistory((prev) => [createHistoryEntry(fileName, parsed, new Date()), ...prev]);
          toast.success(
            parsed.failures.length > 0
              ? `${String(parsed.successes.length)}건을 등록했습니다. 실패 ${String(parsed.failures.length)}건은 등록하지 않았습니다.`
              : `${String(parsed.successes.length)}건을 등록했습니다.`,
          );
        }}
      />

      <DownloadDialog
        open={downloadOpen}
        onOpenChange={setDownloadOpen}
        selectedRows={selectedRows}
        allRows={filtered}
      />
    </ListPage>
  );
}
